/**
 * Watch Episodes – Slider + Dynamic Products + YouTube Modal
 * No heavy dependencies. Vanilla JS, modular IIFE per section instance.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     Helpers
  ───────────────────────────────────────────────────────── */

  /**
   * Extract a YouTube video ID from various URL formats.
   * Supports: youtu.be/, watch?v=, /embed/, /shorts/
   */
  function extractYouTubeId(url) {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
    ];
    for (const re of patterns) {
      const m = url.match(re);
      if (m) return m[1];
    }
    return null;
  }

  /**
   * Build an autoplay embed URL from a video ID.
   */
  function buildEmbedUrl(videoId) {
    return (
      'https://www.youtube.com/embed/' +
      videoId +
      '?autoplay=1&rel=0&playsinline=1&enablejsapi=1'
    );
  }

  /* ─────────────────────────────────────────────────────────
     WatchEpisodes class – one instance per section
  ───────────────────────────────────────────────────────── */

  class WatchEpisodes {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;
      this.autoplay = sectionEl.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(sectionEl.dataset.autoplaySpeed, 10) || 5000;

      // Slider elements
      this.track = sectionEl.querySelector('#we-track-' + this.sectionId);
      this.slides = Array.from(sectionEl.querySelectorAll('.we-slide'));
      this.prevBtn = sectionEl.querySelector('.we-arrow--prev');
      this.nextBtn = sectionEl.querySelector('.we-arrow--next');
      this.dots = Array.from(sectionEl.querySelectorAll('.we-dot'));

      // Products
      this.panels = Array.from(sectionEl.querySelectorAll('.we-products-panel'));

      // Modal
      this.modal = document.getElementById('we-modal-' + this.sectionId);
      this.modalIframe = this.modal
        ? this.modal.querySelector('#we-yt-iframe-' + this.sectionId)
        : null;
      this.modalBackdrop = this.modal
        ? this.modal.querySelector('.we-modal-backdrop')
        : null;
      this.modalCloseBtn = this.modal
        ? this.modal.querySelector('.we-modal-close')
        : null;

      // State
      this.currentIndex = 0;
      this.totalSlides = this.slides.length;
      this.autoplayTimer = null;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDelta = 0;

      if (this.totalSlides === 0) return;

      this._init();
    }

    /* ── Init ─────────────────────────────────────────────── */

    _init() {
      this._bindArrows();
      this._bindDots();
      this._bindTouch();
      this._bindVideoTriggers();
      this._bindModal();
      this._bindHover();
      this._updateA11y();

      if (this.autoplay && this.totalSlides > 1) {
        this._startAutoplay();
      }

      // Lazy-load all images in the section
      this._lazyLoadImages();
    }

    /* ── Slide navigation ─────────────────────────────────── */

    goTo(index, direction) {
      if (index === this.currentIndex) return;

      const prevIndex = this.currentIndex;
      // Clamp
      index = ((index % this.totalSlides) + this.totalSlides) % this.totalSlides;

      const prevSlide = this.slides[prevIndex];
      const nextSlide = this.slides[index];
      const dir = direction !== undefined ? direction : index > prevIndex ? 1 : -1;

      // Prepare entering slide off-screen
      nextSlide.style.transform = dir > 0 ? 'translateX(100%)' : 'translateX(-100%)';
      nextSlide.classList.add('we-slide--entering');
      nextSlide.classList.remove('we-slide--active');

      // Animate
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          prevSlide.style.transform = dir > 0 ? 'translateX(-100%)' : 'translateX(100%)';
          prevSlide.classList.add('we-slide--leaving');
          prevSlide.classList.remove('we-slide--active');

          nextSlide.style.transform = 'translateX(0)';
          nextSlide.classList.add('we-slide--active');
          nextSlide.classList.remove('we-slide--entering');
        });
      });

      const cleanup = () => {
        prevSlide.classList.remove('we-slide--leaving');
        prevSlide.style.transform = '';
      };

      nextSlide.addEventListener('transitionend', cleanup, { once: true });

      this.currentIndex = index;
      this._updateDots();
      this._updateProducts();
      this._updateA11y();
    }

    prev() {
      this.goTo(this.currentIndex - 1, -1);
    }

    next() {
      this.goTo(this.currentIndex + 1, 1);
    }

    /* ── Products panel swap ──────────────────────────────── */

    _updateProducts() {
      this.panels.forEach((panel, i) => {
        const active = i === this.currentIndex;
        panel.classList.toggle('we-products-panel--active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    /* ── Dots ─────────────────────────────────────────────── */

    _updateDots() {
      this.dots.forEach((dot, i) => {
        const active = i === this.currentIndex;
        dot.classList.toggle('we-dot--active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    /* ── Accessibility ────────────────────────────────────── */

    _updateA11y() {
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', String(i !== this.currentIndex));
        slide.setAttribute('tabindex', i === this.currentIndex ? '0' : '-1');
      });
    }

    /* ── Arrow buttons ────────────────────────────────────── */

    _bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this._stopAutoplay();
          this.prev();
          if (this.autoplay) this._startAutoplay();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this._stopAutoplay();
          this.next();
          if (this.autoplay) this._startAutoplay();
        });
      }
    }

    /* ── Dot buttons ──────────────────────────────────────── */

    _bindDots() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.dotIndex, 10);
          this._stopAutoplay();
          this.goTo(idx);
          if (this.autoplay) this._startAutoplay();
        });
      });
    }

    /* ── Touch / swipe ────────────────────────────────────── */

    _bindTouch() {
      const viewport = this.section.querySelector('.we-slides-viewport');
      if (!viewport) return;

      // Touch
      viewport.addEventListener('touchstart', (e) => {
        this.dragStartX = e.touches[0].clientX;
        this.isDragging = true;
        this._stopAutoplay();
      }, { passive: true });

      viewport.addEventListener('touchend', (e) => {
        if (!this.isDragging) return;
        this.dragDelta = e.changedTouches[0].clientX - this.dragStartX;
        this._handleDragEnd();
      });

      // Mouse drag
      viewport.addEventListener('mousedown', (e) => {
        this.dragStartX = e.clientX;
        this.isDragging = true;
        this._stopAutoplay();
        e.preventDefault();
      });

      document.addEventListener('mouseup', (e) => {
        if (!this.isDragging) return;
        this.dragDelta = e.clientX - this.dragStartX;
        this.isDragging = false;
        this._handleDragEnd();
      });
    }

    _handleDragEnd() {
      const THRESHOLD = 50;
      if (this.dragDelta < -THRESHOLD) {
        this.next();
      } else if (this.dragDelta > THRESHOLD) {
        this.prev();
      }
      this.isDragging = false;
      this.dragDelta = 0;
      if (this.autoplay) this._startAutoplay();
    }

    /* ── Video triggers ───────────────────────────────────── */

    _bindVideoTriggers() {
      // Both .we-play-overlay (thumbnail) and .we-watch-btn trigger the modal
      const triggers = this.section.querySelectorAll('[data-youtube]');
      triggers.forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const url = el.dataset.youtube;
          const title = el.dataset.title || '';
          this._openModal(url, title);
        });
      });
    }

    /* ── Modal ────────────────────────────────────────────── */

    _bindModal() {
      if (!this.modal) return;

      // Close via X button
      if (this.modalCloseBtn) {
        this.modalCloseBtn.addEventListener('click', () => this._closeModal());
      }

      // Close via backdrop
      if (this.modalBackdrop) {
        this.modalBackdrop.addEventListener('click', () => this._closeModal());
      }

      // Close via Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) {
          this._closeModal();
        }
      });
    }

    _openModal(youtubeUrl, title) {
      if (!this.modal || !this.modalIframe) return;

      const videoId = extractYouTubeId(youtubeUrl);
      if (!videoId) return;

      this.modalIframe.src = buildEmbedUrl(videoId);
      if (title) this.modalIframe.title = title;

      this.modal.hidden = false;
      this.modal.classList.add('we-modal--open');
      document.body.style.overflow = 'hidden';

      // Focus trap – focus close button
      if (this.modalCloseBtn) {
        setTimeout(() => this.modalCloseBtn.focus(), 50);
      }
    }

    _closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.classList.remove('we-modal--open');
      document.body.style.overflow = '';

      // Stop video by clearing src
      if (this.modalIframe) this.modalIframe.src = '';
    }

    /* ── Autoplay ─────────────────────────────────────────── */

    _startAutoplay() {
      this._stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.next();
      }, this.autoplaySpeed);
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    /* ── Pause on hover ───────────────────────────────────── */

    _bindHover() {
      if (!this.autoplay) return;
      const viewport = this.section.querySelector('.we-slider-wrapper');
      if (!viewport) return;
      viewport.addEventListener('mouseenter', () => this._stopAutoplay());
      viewport.addEventListener('mouseleave', () => this._startAutoplay());
    }

    /* ── Lazy load images ─────────────────────────────────── */

    _lazyLoadImages() {
      if (!('IntersectionObserver' in window)) return;
      const imgs = this.section.querySelectorAll('img[loading="lazy"]');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                delete img.dataset.src;
              }
              observer.unobserve(img);
            }
          });
        },
        { rootMargin: '200px' }
      );
      imgs.forEach((img) => observer.observe(img));
    }
  }

  /* ─────────────────────────────────────────────────────────
     Bootstrap – initialise all sections on the page
  ───────────────────────────────────────────────────────── */

  function initAll() {
    document.querySelectorAll('.watch-episodes-section').forEach((el) => {
      if (!el.__weInstance) {
        el.__weInstance = new WatchEpisodes(el);
      }
    });
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor – re-init when sections are reloaded
  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = e.target.querySelector('.watch-episodes-section');
    if (sectionEl) {
      sectionEl.__weInstance = new WatchEpisodes(sectionEl);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sectionEl = e.target.querySelector('.watch-episodes-section');
    if (sectionEl && sectionEl.__weInstance) {
      sectionEl.__weInstance._stopAutoplay();
      sectionEl.__weInstance._closeModal();
      delete sectionEl.__weInstance;
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const sectionEl = e.target.closest('.watch-episodes-section');
    if (!sectionEl) return;
    const instance = sectionEl.__weInstance;
    if (!instance) return;
    const slide = e.target.closest('.we-slide');
    if (!slide) return;
    const idx = parseInt(slide.dataset.slideIndex, 10);
    instance._stopAutoplay();
    instance.goTo(idx);
  });
})();
